import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import api from '../utils/api'
import { useAuth } from '../hooks/useAuth'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'
import ReportProgress from '../components/ReportProgress'

interface BrokerFinding {
  name: string
  has_data: boolean
  data_types: string[]
  opt_out_url: string
}

interface AISummary {
  headline: string
  summary: string
  threats: string[]
}

interface ReportData {
  id: string
  tier: string
  status: 'pending' | 'processing' | 'ready_for_review' | 'complete' | 'error'
  created_at: string
  pdf_url: string | null
  results: {
    pir_scores: Record<string, { score: number; status: string }>
    broker_findings: BrokerFinding[]
    breach_findings: Array<{
      name: string
      date: string
      data_exposed: string[]
    }>
    risk_score: number
    recommendations: string[]
    ai_summary?: AISummary
  } | null
}

interface OptOutModalState {
  isOpen: boolean
  broker: BrokerFinding | null
  step: 'form' | 'preview' | 'sending' | 'success' | 'error'
  preview: string
  brokerEmail: string
  errorMessage: string
}

interface SentOptOut {
  broker_domain: string
  status: string
}

// Extract domain from opt_out_url
function extractDomain(url: string): string {
  try {
    const urlObj = new URL(url)
    return urlObj.hostname.replace('www.', '')
  } catch {
    return url
  }
}

// US States for dropdown
const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
  'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
  'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
  'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
  'Wisconsin', 'Wyoming'
]

export default function Report() {
  const { id } = useParams()
  const { user } = useAuth()
  const [report, setReport] = useState<ReportData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  // Opt-out modal state
  const [modal, setModal] = useState<OptOutModalState>({
    isOpen: false,
    broker: null,
    step: 'form',
    preview: '',
    brokerEmail: '',
    errorMessage: '',
  })

  // Form fields for opt-out
  const [userName, setUserName] = useState('')
  const [userEmail, setUserEmail] = useState('')
  const [userState, setUserState] = useState('California')
  const [confirmationEmail, setConfirmationEmail] = useState('')
  const [downloadingPdf, setDownloadingPdf] = useState(false)

  // Download PDF with authentication
  async function handleDownloadPdf() {
    if (!report?.pdf_url) return
    setDownloadingPdf(true)
    try {
      const response = await api.get(report.pdf_url, { responseType: 'blob' })
      const blob = new Blob([response.data], { type: 'application/pdf' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `Intelligence_Report_${id}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Failed to download PDF:', err)
      alert('Failed to download PDF. Please try again.')
    } finally {
      setDownloadingPdf(false)
    }
  }

  // Track sent opt-outs
  const [sentOptOuts, setSentOptOuts] = useState<SentOptOut[]>([])

  useEffect(() => {
    if (!id) return

    let timeoutId: ReturnType<typeof setTimeout>

    async function fetchReport() {
      try {
        const response = await api.get(`/report/${id}`)
        setReport(response.data)

        // Keep polling if still pending or processing
        if (response.data.status === 'pending' || response.data.status === 'processing') {
          timeoutId = setTimeout(fetchReport, 3000)
        }
      } catch (err: any) {
        setError('Could not load report. Please try again.')
      } finally {
        setLoading(false)
      }
    }

    async function fetchSentOptOuts() {
      try {
        const response = await api.get('/optout/requests')
        setSentOptOuts(response.data.map((r: any) => ({
          broker_domain: r.broker_domain,
          status: r.status,
        })))
      } catch (err) {
        // Silently fail - not critical
      }
    }

    fetchReport()
    fetchSentOptOuts()

    return () => {
      if (timeoutId) clearTimeout(timeoutId)
    }
  }, [id])

  // Open opt-out modal for a broker - go straight to preview
  const openOptOutModal = async (broker: BrokerFinding) => {
    // Pre-fill with user data
    const email = user?.email || ''
    // Extract name from email as default (e.g., "jstutzman" -> "J Stutzman")
    const emailName = email.split('@')[0] || ''
    const defaultName = emailName.charAt(0).toUpperCase() + ' ' +
      emailName.slice(1).replace(/[0-9]/g, '').split(/[._-]/).map(
        (part: string) => part.charAt(0).toUpperCase() + part.slice(1)
      ).join(' ')

    setUserName(defaultName)
    setUserEmail(email)
    setUserState('California')
    setConfirmationEmail(email)

    // Open modal and immediately fetch preview
    setModal({
      isOpen: true,
      broker,
      step: 'preview',
      preview: 'Loading preview...',
      brokerEmail: '',
      errorMessage: '',
    })

    // Fetch preview automatically
    try {
      const response = await api.post('/optout/preview', {
        broker_name: broker.name,
        broker_domain: extractDomain(broker.opt_out_url),
        data_types: broker.data_types,
        user_name: defaultName,
        user_email: email,
        user_state: 'California',
      })

      setModal(prev => ({
        ...prev,
        preview: response.data.preview_text,
        brokerEmail: response.data.broker_email,
      }))
    } catch (err: any) {
      setModal(prev => ({
        ...prev,
        step: 'error',
        errorMessage: err.response?.data?.detail || 'Failed to generate preview',
      }))
    }
  }

  // Close modal
  const closeModal = () => {
    setModal(prev => ({ ...prev, isOpen: false }))
  }

  // Preview the opt-out request
  const previewOptOut = async () => {
    if (!modal.broker) return

    try {
      const response = await api.post('/optout/preview', {
        broker_name: modal.broker.name,
        broker_domain: extractDomain(modal.broker.opt_out_url),
        data_types: modal.broker.data_types,
        user_name: userName,
        user_email: userEmail,
        user_state: userState,
      })

      setModal(prev => ({
        ...prev,
        step: 'preview',
        preview: response.data.preview_text,
        brokerEmail: response.data.broker_email,
      }))
    } catch (err: any) {
      setModal(prev => ({
        ...prev,
        step: 'error',
        errorMessage: err.response?.data?.detail || 'Failed to generate preview',
      }))
    }
  }

  // Send the opt-out request
  const sendOptOut = async () => {
    if (!modal.broker || !report) return

    setModal(prev => ({ ...prev, step: 'sending' }))

    try {
      const response = await api.post('/optout/send', {
        report_id: report.id,
        broker_name: modal.broker.name,
        broker_domain: extractDomain(modal.broker.opt_out_url),
        data_types: modal.broker.data_types,
        user_name: userName,
        user_email: userEmail,
        user_state: userState,
        confirmation_email: confirmationEmail || userEmail,
      })

      if (response.data.success) {
        setModal(prev => ({ ...prev, step: 'success' }))
        // Add to sent list
        setSentOptOuts(prev => [...prev, {
          broker_domain: extractDomain(modal.broker!.opt_out_url),
          status: 'sent',
        }])
      } else {
        setModal(prev => ({
          ...prev,
          step: 'error',
          errorMessage: response.data.message,
        }))
      }
    } catch (err: any) {
      setModal(prev => ({
        ...prev,
        step: 'error',
        errorMessage: err.response?.data?.detail || 'Failed to send request',
      }))
    }
  }

  // Check if opt-out already sent for this broker
  const isOptOutSent = (broker: BrokerFinding): boolean => {
    const domain = extractDomain(broker.opt_out_url)
    return sentOptOuts.some(s => s.broker_domain === domain)
  }

  if (loading) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="pt-24 flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-ginger-500"></div>
        </div>
      </div>
    )
  }

  if (error || !report) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="pt-24 flex items-center justify-center min-h-screen">
          <div className="card max-w-md text-center">
            <h1 className="text-xl font-bold mb-2">Error Loading Report</h1>
            <p className="text-dark-400 mb-4">{error}</p>
            <Link to="/dashboard" className="btn btn-primary">
              Back to Dashboard
            </Link>
          </div>
        </div>
      </div>
    )
  }

  if (report.status === 'error') {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="pt-24 flex items-center justify-center min-h-screen">
          <div className="card max-w-md text-center">
            <div className="text-4xl mb-4">⚠️</div>
            <h1 className="text-xl font-bold mb-2">Report Generation Failed</h1>
            <p className="text-dark-400 mb-4">
              There was an issue generating your report. Our team has been notified.
            </p>
            <Link to="/dashboard" className="btn btn-primary">
              Back to Dashboard
            </Link>
          </div>
        </div>
      </div>
    )
  }

  if (report.status === 'pending' || report.status === 'processing') {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="pt-24 pb-16 px-4">
          <div className="max-w-2xl mx-auto">
            <div className="card">
              <h1 className="text-2xl font-bold mb-2 text-center">
                {report.status === 'pending' ? 'Report Queued' : 'Generating Your Report'}
              </h1>
              <p className="text-dark-400 mb-8 text-center">
                {report.status === 'pending'
                  ? 'Your report is in the queue and will begin processing shortly.'
                  : 'We\'re running a comprehensive scan across all data sources.'}
              </p>
              <ReportProgress reportId={report.id} />
            </div>
          </div>
        </div>
        <Footer />
      </div>
    )
  }

  const results = report.results!

  return (
    <div className="min-h-screen">
      <Navbar />

      <div className="pt-24 pb-16 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="flex justify-between items-start">
              <div>
                <h1 className="text-3xl font-bold mb-2">
                  {report.tier === 'quick' ? 'Executive Exposure Report' : 'Executive Risk Assessment'}
                </h1>
                <p className="text-dark-400">
                  Generated on {new Date(report.created_at).toLocaleDateString()}
                </p>
              </div>
              {report.pdf_url && (
                <button
                  onClick={handleDownloadPdf}
                  disabled={downloadingPdf}
                  className="btn btn-secondary"
                >
                  {downloadingPdf ? 'Downloading...' : 'Download PDF'}
                </button>
              )}
            </div>

            {/* AI-Generated Executive Summary for QUICK tier */}
            {report.tier === 'quick' && (
              <div className="mt-6 p-6 bg-gradient-to-r from-red-500/10 to-ginger-500/10 border border-red-500/20 rounded-xl">
                <h2 className="text-xl font-bold text-red-400 mb-3">
                  {results.ai_summary?.headline || 'We found you. So can anyone else.'}
                </h2>
                <p className="text-dark-300 leading-relaxed">
                  {results.ai_summary?.summary || (
                    <>In just seconds, we located your personal information across <span className="text-white font-semibold">{results.broker_findings.filter(b => b.has_data).length} data brokers</span> and found your credentials in <span className="text-white font-semibold">{results.breach_findings.length} data breaches</span>.</>
                  )}
                </p>

                {/* AI-Generated Threat Scenarios */}
                {results.ai_summary?.threats && results.ai_summary.threats.length > 0 && (
                  <div className="mt-4 space-y-2">
                    <p className="text-dark-400 text-sm font-medium">What adversaries could do with this information:</p>
                    <ul className="space-y-2">
                      {results.ai_summary.threats.map((threat, i) => (
                        <li key={i} className="flex items-start gap-2 text-dark-300 text-sm">
                          <span className="text-red-400 mt-0.5">⚠</span>
                          <span>{threat}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <p className="text-dark-400 mt-4 text-sm">
                  This Quick Check shows where you're exposed. A Full Assessment reveals the complete picture - including social media vulnerabilities, public records, asset registrations, and AI-powered threat analysis.
                </p>
              </div>
            )}
          </div>

          {/* Upsell banner for QUICK tier */}
          {report.tier === 'quick' && (
            <div className="card mb-8 border-ginger-500/30 bg-gradient-to-r from-dark-900 to-dark-800">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div>
                  <h3 className="text-lg font-bold text-ginger-400">Want the complete picture?</h3>
                  <p className="text-dark-400 text-sm mt-1">
                    Upgrade to a Full Assessment for comprehensive threat analysis, social media scanning, public records search, and a professionally written intelligence report.
                  </p>
                </div>
                <a href="/#pricing" className="btn btn-primary whitespace-nowrap">
                  Upgrade to Full Assessment
                </a>
              </div>
            </div>
          )}

          {/* Risk Score */}
          <div className="card mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold mb-2">Your Privacy Risk Score</h2>
                <p className="text-dark-300 text-sm mb-2">
                  This number shows how easy it would be for someone to find your personal information online.
                </p>
                <p className="text-dark-400 text-sm">
                  {results.risk_score >= 70
                    ? 'Your score is HIGH. This means your personal data is widely available and you should take action to protect yourself.'
                    : results.risk_score >= 40
                    ? 'Your score is MODERATE. Some of your information is exposed, but you can reduce your risk by following our recommendations.'
                    : 'Your score is LOW. Your information has limited exposure, but staying vigilant is still important.'}
                </p>
              </div>
              <div className="text-center">
                <div className={`text-5xl font-bold ${
                  results.risk_score >= 70 ? 'text-red-500' :
                  results.risk_score >= 40 ? 'text-yellow-500' : 'text-green-500'
                }`}>
                  {results.risk_score}
                </div>
                <div className="text-dark-500 text-sm">/ 100</div>
                <div className={`text-xs mt-1 ${
                  results.risk_score >= 70 ? 'text-red-400' :
                  results.risk_score >= 40 ? 'text-yellow-400' : 'text-green-400'
                }`}>
                  {results.risk_score >= 70 ? 'High Risk' : results.risk_score >= 40 ? 'Moderate Risk' : 'Lower Risk'}
                </div>
              </div>
            </div>
          </div>

          {/* PIR Scores */}
          <div className="card mb-8">
            <h2 className="text-xl font-bold mb-2">Where You're Most Exposed</h2>
            <p className="text-dark-300 text-sm mb-2">
              We check 7 key areas where your personal information might be at risk. Each score shows how much of your data we found in that category.
            </p>
            <p className="text-dark-400 text-sm mb-6">
              <span className="text-red-400">Red = High exposure</span> · <span className="text-yellow-400">Yellow = Some exposure</span> · <span className="text-green-400">Green = Limited exposure</span>
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {Object.entries(results.pir_scores).map(([name, data]) => (
                <div
                  key={name}
                  className={`p-4 rounded-lg border ${
                    data.score >= 70 ? 'bg-red-500/10 border-red-500/30' :
                    data.score >= 40 ? 'bg-yellow-500/10 border-yellow-500/30' :
                    'bg-green-500/10 border-green-500/30'
                  }`}
                >
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-sm">{name}</span>
                    <span className="text-xl font-bold">{data.score}</span>
                  </div>
                  <div className={`text-xs mt-1 ${
                    data.score >= 70 ? 'text-red-400' :
                    data.score >= 40 ? 'text-yellow-400' : 'text-green-400'
                  }`}>
                    {data.status}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Broker Findings */}
          <div className="card mb-8">
            {/* Explanation box */}
            <div className="bg-dark-800/50 border border-dark-700 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-dark-200 mb-2">What are data brokers?</h3>
              <p className="text-dark-400 text-sm">
                Data brokers are companies that collect and sell your personal information. They gather data from public records, social media, shopping habits, and other sources. Then they sell it to anyone willing to pay — marketers, employers, landlords, or even criminals. <span className="text-dark-300">The good news: you have the legal right to demand they delete your data.</span>
              </p>
            </div>

            <div className="flex items-start justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold">Sites Selling Your Data</h2>
                <p className="text-dark-300 text-sm mt-1">
                  We found your personal information on <span className="text-red-400 font-semibold">{results.broker_findings.filter(b => b.has_data).length} websites</span> that sell people's data.
                </p>
                <p className="text-dark-400 text-sm mt-1">Anyone can buy this information for a few dollars.</p>
              </div>
              <div className="bg-ginger-500/10 border border-ginger-500/30 rounded-lg px-4 py-3 max-w-xs">
                <div className="flex items-center gap-2 text-ginger-500 font-semibold text-sm">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  We Can Remove You
                </div>
                <p className="text-dark-400 text-xs mt-1">Click "Request Removal" and we'll send a legal deletion request for you</p>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-dark-700">
                    <th className="text-left py-3 px-4 text-dark-400 font-medium">Broker</th>
                    <th className="text-left py-3 px-4 text-dark-400 font-medium">Data Found</th>
                    <th className="text-left py-3 px-4 text-dark-400 font-medium">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {results.broker_findings.filter(b => b.has_data).map((broker, i) => (
                    <tr key={i} className="border-b border-dark-800">
                      <td className="py-3 px-4 font-medium">{broker.name}</td>
                      <td className="py-3 px-4 text-dark-400 text-sm">
                        {broker.data_types.join(', ')}
                      </td>
                      <td className="py-3 px-4">
                        {isOptOutSent(broker) ? (
                          <span className="text-green-500 text-sm flex items-center gap-1">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Request Sent
                          </span>
                        ) : (
                          <div className="flex gap-2">
                            <button
                              onClick={() => openOptOutModal(broker)}
                              className="text-ginger-500 hover:text-ginger-400 text-sm font-medium"
                            >
                              Request Removal
                            </button>
                            <span className="text-dark-600">|</span>
                            <a
                              href={broker.opt_out_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-dark-400 hover:text-dark-300 text-sm"
                            >
                              Manual
                            </a>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Breach Findings */}
          <div className="card mb-8">
            {/* Explanation box */}
            <div className="bg-dark-800/50 border border-dark-700 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-dark-200 mb-2">What is a data breach?</h3>
              <p className="text-dark-400 text-sm">
                A data breach happens when hackers break into a company's systems and steal customer information. If you had an account with that company, your data may have been stolen. This often includes passwords, email addresses, and sometimes credit card numbers or social security numbers. <span className="text-dark-300">Stolen data is often sold on the dark web or used for identity theft.</span>
              </p>
            </div>

            <h2 className="text-xl font-bold mb-2">Your Data Was Stolen in These Breaches</h2>
            <p className="text-dark-300 text-sm mb-2">
              Your information appeared in <span className="text-red-400 font-semibold">{results.breach_findings.length} known data breaches</span>.
            </p>
            <p className="text-dark-400 text-sm mb-6">
              If you used the same password on multiple sites, hackers may be able to access your other accounts too.
            </p>
            <div className="space-y-4">
              {results.breach_findings.map((breach, i) => (
                <div key={i} className="bg-dark-800 rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-bold">{breach.name}</h3>
                      <p className="text-dark-500 text-sm">{breach.date}</p>
                    </div>
                    <span className="bg-red-500/20 text-red-400 text-xs px-2 py-1 rounded">
                      Exposed
                    </span>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {breach.data_exposed.map((type, j) => (
                      <span key={j} className="bg-dark-700 text-dark-300 text-xs px-2 py-1 rounded">
                        {type}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Recommendations */}
          <div className="card">
            <h2 className="text-xl font-bold mb-2">What You Should Do Now</h2>
            <p className="text-dark-300 text-sm mb-2">
              Here are the most important steps to protect yourself, listed in order of priority.
            </p>
            <p className="text-dark-400 text-sm mb-6">
              Start with #1 and work your way down. Even completing just the first few steps will significantly reduce your risk.
            </p>
            <div className="space-y-4">
              {results.recommendations.map((rec, i) => (
                <div key={i} className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-ginger-500 flex items-center justify-center font-bold text-sm">
                    {i + 1}
                  </div>
                  <div className="pt-1">
                    <p className="text-dark-300">{rec}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Need Help CTA */}
            <div className="mt-8 p-6 bg-gradient-to-r from-ginger-500/10 to-dark-800 border border-ginger-500/30 rounded-xl">
              <h3 className="text-lg font-bold text-ginger-400 mb-2">Not sure how to do all this?</h3>
              <p className="text-dark-300 text-sm mb-4">
                You're not alone. Most people don't have time to send dozens of removal requests, change all their passwords, or figure out how to freeze their credit. That's exactly why we're here.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <p className="text-dark-400 text-sm">
                    <span className="text-white font-medium">Our Full Assessment</span> includes step-by-step guidance, priority support, and we handle the hard work for you — removing your data from brokers, monitoring for new exposures, and keeping you protected.
                  </p>
                </div>
                <div className="flex-shrink-0">
                  <a href="/#pricing" className="btn btn-primary whitespace-nowrap">
                    Get Help Now
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Final CTA for QUICK tier */}
          {report.tier === 'quick' && (
            <div className="card mt-8 text-center">
              <h2 className="text-2xl font-bold mb-3">This is just the surface.</h2>
              <p className="text-dark-300 mb-2">
                A Quick Check shows where your data is being sold. But there's more you don't see yet.
              </p>
              <p className="text-dark-400 text-sm mb-6">
                Our Full Assessment scans social media, public records, court documents, property records, and uses AI to identify threats specific to you. Plus, we help you fix everything we find.
              </p>
              <a href="/#pricing" className="btn btn-primary btn-lg">
                Upgrade to Full Assessment
              </a>
              <p className="text-dark-500 text-xs mt-4">
                Questions? Email us at <a href="mailto:support@monadnockcyber.ai" className="text-ginger-500 hover:underline">support@monadnockcyber.ai</a>
              </p>
            </div>
          )}
        </div>
      </div>

      <Footer />

      {/* Opt-Out Modal */}
      {modal.isOpen && modal.broker && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-dark-900 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-dark-700">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-bold">
                  {modal.step === 'form' && 'Send Opt-Out Request'}
                  {modal.step === 'preview' && 'Review Request Before Sending'}
                  {modal.step === 'sending' && 'Sending Request...'}
                  {modal.step === 'success' && 'Request Sent!'}
                  {modal.step === 'error' && 'Error'}
                </h3>
                <button onClick={closeModal} className="text-dark-400 hover:text-white">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p className="text-dark-400 mt-1">
                {modal.broker.name} ({extractDomain(modal.broker.opt_out_url)})
              </p>
            </div>

            <div className="p-6">
              {/* Form Step */}
              {modal.step === 'form' && (
                <div className="space-y-4">
                  <p className="text-dark-300 text-sm mb-4">
                    We'll send a formal CCPA/GDPR data deletion request to {modal.broker.name} on your behalf.
                    Please provide your information below.
                  </p>

                  <div>
                    <label className="block text-sm font-medium mb-1">Your Full Name *</label>
                    <input
                      type="text"
                      value={userName}
                      onChange={(e) => setUserName(e.target.value)}
                      className="w-full bg-dark-800 border border-dark-700 rounded-lg px-4 py-2 focus:outline-none focus:border-ginger-500"
                      placeholder="John Smith"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Email for Broker to Confirm To *</label>
                    <input
                      type="email"
                      value={userEmail}
                      onChange={(e) => setUserEmail(e.target.value)}
                      className="w-full bg-dark-800 border border-dark-700 rounded-lg px-4 py-2 focus:outline-none focus:border-ginger-500"
                      placeholder="you@example.com"
                    />
                    <p className="text-dark-500 text-xs mt-1">
                      This is included in the request so the broker can confirm deletion
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">State of Residence *</label>
                    <select
                      value={userState}
                      onChange={(e) => setUserState(e.target.value)}
                      className="w-full bg-dark-800 border border-dark-700 rounded-lg px-4 py-2 focus:outline-none focus:border-ginger-500"
                    >
                      {US_STATES.map(state => (
                        <option key={state} value={state}>{state}</option>
                      ))}
                    </select>
                    <p className="text-dark-500 text-xs mt-1">
                      California residents have strongest CCPA protections
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Send Confirmation To (optional)</label>
                    <input
                      type="email"
                      value={confirmationEmail}
                      onChange={(e) => setConfirmationEmail(e.target.value)}
                      className="w-full bg-dark-800 border border-dark-700 rounded-lg px-4 py-2 focus:outline-none focus:border-ginger-500"
                      placeholder="Leave blank to use email above"
                    />
                    <p className="text-dark-500 text-xs mt-1">
                      We'll send you a confirmation when the request is sent
                    </p>
                  </div>

                  <div className="bg-dark-800 rounded-lg p-4 mt-4">
                    <p className="text-sm text-dark-400">
                      <strong className="text-white">Data to be removed:</strong><br />
                      {modal.broker.data_types.join(', ')}
                    </p>
                  </div>

                  <div className="flex gap-3 mt-6">
                    <button
                      onClick={closeModal}
                      className="btn btn-secondary flex-1"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={previewOptOut}
                      disabled={!userName || !userEmail}
                      className="btn btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Preview Request
                    </button>
                  </div>
                </div>
              )}

              {/* Preview Step */}
              {modal.step === 'preview' && (
                <div>
                  <div className="bg-dark-800 rounded-lg p-4 mb-4">
                    <div className="flex justify-between items-center">
                      <p className="text-sm text-dark-400">
                        <strong className="text-white">Sending to:</strong> {modal.brokerEmail || 'Loading...'}
                      </p>
                      <button
                        onClick={() => setModal(prev => ({ ...prev, step: 'form' }))}
                        className="text-ginger-500 hover:text-ginger-400 text-sm"
                      >
                        Edit Details
                      </button>
                    </div>
                  </div>

                  <div className="bg-white text-black rounded-lg p-4 max-h-80 overflow-y-auto">
                    <pre className="whitespace-pre-wrap text-sm font-mono">
                      {modal.preview}
                    </pre>
                  </div>

                  <div className="flex gap-3 mt-6">
                    <button
                      onClick={closeModal}
                      className="btn btn-secondary flex-1"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={sendOptOut}
                      disabled={!modal.brokerEmail}
                      className="btn btn-primary flex-1 disabled:opacity-50"
                    >
                      Approve & Send
                    </button>
                  </div>
                </div>
              )}

              {/* Sending Step */}
              {modal.step === 'sending' && (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-ginger-500 mx-auto mb-4"></div>
                  <p className="text-dark-400">Sending your opt-out request...</p>
                </div>
              )}

              {/* Success Step */}
              {modal.step === 'success' && (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h4 className="text-xl font-bold mb-2">Request Sent Successfully!</h4>
                  <p className="text-dark-400 mb-4">
                    We've sent a formal CCPA/GDPR deletion request to {modal.broker.name}.
                    They have 45 days to comply.
                  </p>
                  <p className="text-dark-500 text-sm mb-6">
                    A confirmation email has been sent to {confirmationEmail || userEmail}.
                  </p>
                  <button onClick={closeModal} className="btn btn-primary">
                    Done
                  </button>
                </div>
              )}

              {/* Error Step */}
              {modal.step === 'error' && (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </div>
                  <h4 className="text-xl font-bold mb-2">Something Went Wrong</h4>
                  <p className="text-dark-400 mb-6">{modal.errorMessage}</p>
                  <div className="flex gap-3 justify-center">
                    <button onClick={closeModal} className="btn btn-secondary">
                      Close
                    </button>
                    <button
                      onClick={() => setModal(prev => ({ ...prev, step: 'form' }))}
                      className="btn btn-primary"
                    >
                      Try Again
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
